import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { RECENTLY_VIEWED_LIMIT } from "@/lib/constants";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const read = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) || d; } catch { return d; } };

export function StoreProvider({ children }) {
  const [cart, setCart] = useState(() => read("gulit_cart", []));
  const [wishlist, setWishlist] = useState(() => read("gulit_wishlist", []));
  const [recent, setRecent] = useState(() => read("gulit_recent", []));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => localStorage.setItem("gulit_cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("gulit_wishlist", JSON.stringify(wishlist)), [wishlist]);
  useEffect(() => localStorage.setItem("gulit_recent", JSON.stringify(recent)), [recent]);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((c) => {
      const ex = c.find((i) => i.id === product.id);
      if (ex) return c.map((i) => (i.id === product.id ? { ...i, quantity: i.quantity + qty } : i));
      return [...c, { id: product.id, name: product.name, price: product.price, image: product.images?.[0], slug: product.slug, quantity: qty }];
    });
    toast.success(`${product.name} added to cart`);
    setCartOpen(true);
  }, []);

  const updateQty = useCallback((id, qty) => {
    if (qty < 1) return setCart((c) => c.filter((i) => i.id !== id));
    setCart((c) => c.map((i) => (i.id === id ? { ...i, quantity: qty } : i)));
  }, []);
  const removeFromCart = useCallback((id) => setCart((c) => c.filter((i) => i.id !== id)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((w) => {
      if (w.find((i) => i.id === product.id)) {
        toast("Removed from wishlist");
        return w.filter((i) => i.id !== product.id);
      }
      toast.success("Saved to wishlist");
      return [...w, { id: product.id, name: product.name, price: product.price, image: product.images?.[0], slug: product.slug }];
    });
  }, []);
  const inWishlist = useCallback((id) => wishlist.some((i) => i.id === id), [wishlist]);

  const addRecent = useCallback((product) => {
    setRecent((r) => [{ id: product.id, name: product.name, price: product.price, image: product.images?.[0], slug: product.slug },
      ...r.filter((i) => i.id !== product.id)].slice(0, RECENTLY_VIEWED_LIMIT));
  }, []);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  const value = useMemo(() => ({ cart, cartCount, cartTotal, addToCart, updateQty, removeFromCart, clearCart,
    wishlist, toggleWishlist, inWishlist, recent, addRecent, cartOpen, setCartOpen }),
    [cart, cartCount, cartTotal, addToCart, updateQty, removeFromCart, clearCart,
     wishlist, toggleWishlist, inWishlist, recent, addRecent, cartOpen]);

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
